/*
Unified Notifications Dropdown Component
Shows breakdown of notification types with navigation
Author: AI Assistant
Date: 2025-01-27
*/
import { Badge, Button, Card, DropdownMenu, Flex, Text } from '@radix-ui/themes'
import { BellIcon, MessagesSquareIcon, HandshakeIcon, XCircleIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useState, useRef, useEffect, useCallback } from 'react'

import { UnifiedNotificationCounts } from '@/hooks/useUnifiedNotifications'
import { AppPath } from '@/enums/AppPath'

interface UnifiedNotificationsDropdownProps {
  counts: UnifiedNotificationCounts
  isConnected: boolean
  onClearNotifications?: () => void
}

export const UnifiedNotificationsDropdown: React.FC<UnifiedNotificationsDropdownProps> = ({
  counts,
  isConnected,
  onClearNotifications,
}) => {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleNavigateToMessages = () => {
    navigate(`/${AppPath.Messages}`)
    setIsOpen(false)
  }

  const handleNavigateToDeals = () => {
    navigate(`/${AppPath.Deals}`)
    setIsOpen(false)
  }

  const handleClearNotifications = () => {
    if (onClearNotifications) {
      onClearNotifications()
      setIsOpen(false) // Close dropdown after clearing
    }
  }

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!containerRef.current) return

      const container = containerRef.current
      const containerRect = container.getBoundingClientRect()

      // Start with trigger area
      let isInHoverZone =
        event.clientX >= containerRect.left - 10 &&
        event.clientX <= containerRect.right + 10 &&
        event.clientY >= containerRect.top - 10 &&
        event.clientY <= containerRect.bottom + 10

      // If dropdown is open and content ref exists, include content area
      if (isOpen && contentRef.current) {
        const contentRect = contentRef.current.getBoundingClientRect()
        const isInContentArea =
          event.clientX >= contentRect.left - 10 &&
          event.clientX <= contentRect.right + 10 &&
          event.clientY >= contentRect.top - 10 &&
          event.clientY <= contentRect.bottom + 10

        // Also include the path between trigger and content (for diagonal mouse movement)
        const isInPathArea =
          event.clientX >= Math.min(containerRect.left, contentRect.left) - 10 &&
          event.clientX <= Math.max(containerRect.right, contentRect.right) + 10 &&
          event.clientY >= containerRect.top - 10 &&
          event.clientY <= contentRect.bottom + 10

        isInHoverZone = isInHoverZone || isInContentArea || isInPathArea
      }

      if (isInHoverZone) {
        // Clear close timeout and open dropdown
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current)
          closeTimeoutRef.current = null
        }
        setIsOpen(true)
      } else {
        // Start close timeout if not already started
        if (!closeTimeoutRef.current && isOpen) {
          closeTimeoutRef.current = setTimeout(() => {
            setIsOpen(false)
            closeTimeoutRef.current = null
          }, 100)
        }
      }
    },
    [isOpen]
  )

  // Add mouse move listener when component mounts
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)

      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
      }
    }
  }, [handleMouseMove])

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger>
        <div
          ref={containerRef}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '6px',
          }}
        >
          <Button variant="ghost">
            <BellIcon />
          </Button>
          {counts.total > 0 && (
            <Badge color="red" size="1">
              {counts.total}
            </Badge>
          )}
        </div>
      </DropdownMenu.Trigger>

      <DropdownMenu.Content align="end" side="bottom" sideOffset={4}>
        <Card ref={contentRef} style={{ minWidth: '280px', padding: '12px' }}>
          <Flex direction="column" gap="3">
            {/* Header */}
            <Flex align="center" justify="between">
              <Text size="3" weight="bold">
                Notificaciones
              </Text>
              <Flex align="center" gap="2">
                {/* Clear button - only show when there are notifications */}
                {counts.total > 0 && onClearNotifications && (
                  <Button
                    size="1"
                    variant="ghost"
                    onClick={handleClearNotifications}
                    style={{
                      padding: '4px',
                      minWidth: 'auto',
                      height: 'auto',
                    }}
                  >
                    <XCircleIcon size={14} />
                  </Button>
                )}
                <Flex align="center" gap="1">
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: isConnected ? '#22c55e' : '#ef4444',
                    }}
                  />
                  <Text size="1" color="gray">
                    {isConnected ? 'Conectado' : 'Desconectado'}
                  </Text>
                </Flex>
              </Flex>
            </Flex>

            {counts.total === 0 ? (
              /* No notifications */
              <Text size="2" color="gray" style={{ textAlign: 'center', padding: '20px 0' }}>
                No tienes nuevos mensajes o negocios desde que ingresaste
              </Text>
            ) : (
              /* Notification categories */
              <Flex direction="column" gap="2">
                {/* New Messages Since Login */}
                {counts.newMessagesSinceLogin > 0 && (
                  <Button
                    variant="ghost"
                    onClick={handleNavigateToMessages}
                    style={{
                      justifyContent: 'flex-start',
                      padding: '8px 12px',
                      height: 'auto',
                    }}
                  >
                    <Flex align="center" justify="between" width="100%">
                      <Flex align="center" gap="2">
                        <MessagesSquareIcon size={16} />
                        <Text size="2">Mensajes nuevos</Text>
                      </Flex>
                      <Badge color="blue" size="1">
                        {counts.newMessagesSinceLogin}
                      </Badge>
                    </Flex>
                  </Button>
                )}

                {/* New Deals Since Login */}
                {counts.newDealsSinceLogin > 0 && (
                  <Button
                    variant="ghost"
                    onClick={handleNavigateToDeals}
                    style={{
                      justifyContent: 'flex-start',
                      padding: '8px 12px',
                      height: 'auto',
                    }}
                  >
                    <Flex align="center" justify="between" width="100%">
                      <Flex align="center" gap="2">
                        <HandshakeIcon size={16} />
                        <Text size="2">Negocios nuevos</Text>
                      </Flex>
                      <Badge color="green" size="1">
                        {counts.newDealsSinceLogin}
                      </Badge>
                    </Flex>
                  </Button>
                )}
              </Flex>
            )}

            {/* Footer - Quick actions */}
            {counts.total > 0 && (
              <>
                <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '8px 0' }} />
                <Flex gap="2">
                  <Button size="1" variant="soft" onClick={handleNavigateToMessages} style={{ flex: 1 }}>
                    Ver mensajes
                  </Button>
                  <Button size="1" variant="soft" onClick={handleNavigateToDeals} style={{ flex: 1 }}>
                    Ver negocios
                  </Button>
                </Flex>
              </>
            )}
          </Flex>
        </Card>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}
